import { api } from './api';
import { Flyer, FlyerPage, PaginatedResponse } from '../types';

export interface ErpAction {
  id: number;
  name: string;
  validFrom?: string;
  validTo?: string;
}

export interface CreateFlyerDTO {
  name: string;
  actionId?: number;
  actionName?: string;
  validFrom: string;
  validTo: string;
}

export interface UpdateFlyerDTO {
  name?: string;
  actionId?: number;
  actionName?: string;
  validFrom?: string;
  validTo?: string;
  pages?: FlyerPage[];
}

export interface FlyerFilters {
  status?: string;
  page?: number;
  limit?: number;
}

export const flyersService = {
  async getFlyers(filters?: FlyerFilters): Promise<PaginatedResponse<Flyer>> {
    const response = await api.get<PaginatedResponse<Flyer>>('/flyers', { params: filters });
    return response.data;
  },

  async getFlyer(id: string): Promise<Flyer> {
    const response = await api.get<Flyer>(`/flyers/${id}`);
    return response.data;
  },

  // Prodejní akce z ERP (filtrované backendem na značky dodavatele) pro výběr na letáku.
  async getActions(): Promise<ErpAction[]> {
    const response = await api.get<ErpAction[]>('/flyers/actions');
    return response.data;
  },

  async createFlyer(data: CreateFlyerDTO): Promise<Flyer> {
    const response = await api.post<Flyer>('/flyers', data);
    return response.data;
  },

  async updateFlyer(id: string, data: UpdateFlyerDTO): Promise<Flyer> {
    const response = await api.patch<Flyer>(`/flyers/${id}`, data);
    return response.data;
  },

  async deleteFlyer(id: string): Promise<void> {
    await api.delete(`/flyers/${id}`);
  },

  async saveDraft(id: string, data: UpdateFlyerDTO): Promise<Flyer> {
    const response = await api.put<Flyer>(`/flyers/${id}/draft`, data);
    return response.data;
  },

  async submitForApproval(id: string): Promise<Flyer> {
    const response = await api.post<Flyer>(`/flyers/${id}/submit-for-verification`);
    return response.data;
  },

  async getMyFlyers(): Promise<Flyer[]> {
    const response = await api.get<PaginatedResponse<Flyer>>('/flyers');
    return response.data.data; // Extract flyers array from paginated response
  },

  async getActiveFlyers(): Promise<Flyer[]> {
    const response = await api.get<Flyer[]>('/flyers/active');
    return response.data;
  },

  async getExpiredFlyers(): Promise<Flyer[]> {
    const response = await api.get<Flyer[]>('/flyers/expired');
    return response.data;
  },

  async generatePdf(id: string): Promise<{ message: string; url: string }> {
    const response = await api.post<{ message: string; url: string }>(`/flyers/${id}/generate-pdf`);
    return response.data;
  },

  async getPdfBlob(id: string, bustCache: boolean = false): Promise<Blob> {
    const url = bustCache
      ? `/flyers/${id}/pdf?t=${Date.now()}`
      : `/flyers/${id}/pdf`;
    const response = await api.get(url, {
      responseType: 'blob',
    });
    return response.data;
  },

  async expireFlyer(id: string): Promise<Flyer> {
    const response = await api.post<Flyer>(`/flyers/${id}/expire`);
    return response.data;
  },

  // Prodejce: archivace vlastního konceptu (přesun do Archivu) a obnovení zpět.
  async archiveFlyer(id: string): Promise<{ id: string; status: string }> {
    const response = await api.post(`/flyers/${id}/archive`);
    return response.data;
  },

  async unarchiveFlyer(id: string): Promise<{ id: string; status: string }> {
    const response = await api.post(`/flyers/${id}/unarchive`);
    return response.data;
  },

  async validateFlyer(id: string): Promise<{
    valid: boolean;
    errors: Array<{
      productId: string;
      productName: string;
      eanCode: string;
      errors: string[];
      erpPrice?: number;
      erpOriginalPrice?: number;
      currentPrice?: number;
      currentOriginalPrice?: number;
    }>;
    productsChecked: number;
    errorsFound: number;
  }> {
    const response = await api.post(`/flyers/${id}/validate`);
    return response.data;
  },
};
